import React, { useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Chip, Paper, Stack } from '@mui/material';
import { Clock, CheckCircle2, XCircle, AlertCircle, Trash2 } from 'lucide-react';
import { tokens } from '@/theme/tokens';
import useLeaveStore from '@/store/leaveStore';
import { format, parseISO } from 'date-fns';

export function LeaveTimelineModal({ open, onClose, leaveId, leaveData, onCancel }) {
  const { currentDetail, fetchLeaveDetail } = useLeaveStore();

  useEffect(() => {
    // Only fetch from API if leaveId is passed as primitive ID and leaveData object is NOT provided
    if (open && leaveId && typeof leaveId !== 'object' && !leaveData) {
      fetchLeaveDetail(leaveId).catch(() => {});
    }
  }, [open, leaveId, leaveData]);

  const detail = leaveData || (typeof leaveId === 'object' ? leaveId : currentDetail);

  const getStatusChip = (status) => {
    const s = String(status || '').toUpperCase();
    switch (s) {
      case 'APPROVED':
        return <Chip label="Approved" color="success" size="small" icon={<CheckCircle2 size={14} />} />;
      case 'REJECTED':
        return <Chip label="Rejected" color="error" size="small" icon={<XCircle size={14} />} />;
      case 'CANCELLED':
        return <Chip label="Cancelled" color="default" size="small" icon={<AlertCircle size={14} />} />;
      case 'PENDING':
      default:
        return <Chip label="Pending" color="warning" size="small" icon={<Clock size={14} />} />;
    }
  };

  const formatDateStr = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const d = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return format(d, 'dd MMM yyyy, hh:mm a');
    } catch {
      return dateStr;
    }
  };

  const staffName = detail?.staffName || detail?.staff_name || detail?.teacherName || 'Staff Member';
  const employeeId = detail?.employeeId || detail?.employee_id || '';
  const leaveType = typeof detail?.leave_type === 'object' ? detail?.leave_type?.name : (detail?.leaveType || detail?.leave_type || 'Leave');
  const fromDate = detail?.fromDate || detail?.from_date || '-';
  const toDate = detail?.toDate || detail?.to_date || '-';
  const numberOfDays = detail?.numberOfDays || detail?.number_of_days || detail?.days || 1;
  const reason = detail?.reason || '-';
  const status = detail?.status || 'Pending';
  const appliedOn = detail?.appliedOn || detail?.applied_on || '-';
  const remarks = detail?.remarks || detail?.admin_remarks;
  const rejectionReason = detail?.rejectionReason || detail?.rejection_reason;
  const attachmentUrl = detail?.attachmentUrl || detail?.attachment_url || detail?.attachment;
  const canCancel = String(status).toUpperCase() !== 'CANCELLED' && String(status).toUpperCase() !== 'REJECTED';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth paper={{ sx: { borderRadius: tokens.radius.card } }}>
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Leave Request Details</span>
        {status && getStatusChip(status)}
      </DialogTitle>

      <DialogContent dividers sx={{ py: 3 }}>
        {!detail ? (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
            No detail available for this leave request.
          </Typography>
        ) : (
          <Stack spacing={3}>
            {/* Staff Info Section */}
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: tokens.radius.card, bgcolor: tokens.colors.background.neutral }}>
              <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ mb: 1.5 }}>
                APPLICANT DETAILS
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    STAFF MEMBER
                  </Typography>
                  <Typography variant="body1" fontWeight={700} sx={{ mt: 0.5 }}>
                    {staffName}
                  </Typography>
                </Box>

                {employeeId && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      EMPLOYEE ID
                    </Typography>
                    <Typography variant="body1" fontWeight={700} sx={{ mt: 0.5 }}>
                      {employeeId}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Leave Details Card */}
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: tokens.radius.card }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                LEAVE APPLICATION DETAILS
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    LEAVE TYPE
                  </Typography>
                  <Typography variant="body1" fontWeight={700} sx={{ mt: 0.5 }}>
                    {leaveType}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    DURATION / DAYS
                  </Typography>
                  <Typography variant="body1" fontWeight={700} sx={{ mt: 0.5 }}>
                    {numberOfDays} Day(s) ({fromDate} to {toDate})
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    APPLIED ON
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {formatDateStr(appliedOn)}
                  </Typography>
                </Box>
              </Box>

              {reason && (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${tokens.colors.border}` }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    REASON
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {reason}
                  </Typography>
                </Box>
              )}

              {attachmentUrl && (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${tokens.colors.border}` }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    ATTACHMENT
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <a href={attachmentUrl} target="_blank" rel="noopener noreferrer" style={{ color: tokens.colors.primary.main }}>
                      View Attached Document
                    </a>
                  </Typography>
                </Box>
              )}

              {remarks && (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${tokens.colors.border}` }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    ADMIN REMARKS
                  </Typography>
                  <Typography variant="body2" color="primary.main" fontWeight={500} sx={{ mt: 0.5 }}>
                    {remarks}
                  </Typography>
                </Box>
              )}

              {rejectionReason && (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${tokens.colors.border}` }}>
                  <Typography variant="caption" color="error.main" fontWeight={600}>
                    REJECTION REASON
                  </Typography>
                  <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                    {rejectionReason}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
          {canCancel && onCancel && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Trash2 size={16} />}
              onClick={() => {
                onCancel(detail.id);
                onClose();
              }}
            >
              Cancel Leave Request
            </Button>
          )}
        </Box>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
